import { ApplicationConfig, ErrorHandler, importProvidersFrom, inject, provideEnvironmentInitializer } from "@angular/core";
import { provideAnimations } from "@angular/platform-browser/animations";
import { DatePipe } from "@angular/common";
import {
  provideHttpClient,
  withInterceptors,
} from "@angular/common/http";
import { provideRouter } from "@angular/router";
import { provideFirebaseApp, initializeApp, getApp } from "@angular/fire/app";
import { provideAuth, getAuth } from "@angular/fire/auth";
import { LoggerModule } from "ngx-logger";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatDialogModule } from "@angular/material/dialog";
import { provideQueryClient, QueryClient } from "@ngneat/query";
import { environment } from "@environment/environment";
import { timeoutInterceptor, DEFAULT_TIMEOUT } from "./utils/util/timeout.interceptor";
import { ARIA_TOAST_SERVICE } from "./tokens/toast.tokens";
import { AriaToastrInformationService } from "./services/toastr/aria-toastr-information.service";
import { HttpDevToolsErrorHandler } from "./services/dev-tools/http-dev-tools-error.handler";
import { HttpDevToolsService } from "./services/dev-tools/http-dev-tools.service";
import { authInterceptor } from "./interceptors/auth.interceptor";
import { httpDevToolsInterceptor } from "./interceptors/http-dev-tools.interceptor";
import { errorInterceptor } from "./interceptors/error.interceptor";
import { mockCaseInterceptor } from "./interceptors/mock-case.interceptor";
import { clearLegacyCacheKeys } from "./utils/util/legacy-cache-cleanup";
import { routes } from "./app.routes";

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideHttpClient(
      withInterceptors([authInterceptor, httpDevToolsInterceptor, errorInterceptor, mockCaseInterceptor, timeoutInterceptor])
    ),
    provideRouter(routes),
    provideFirebaseApp(() => initializeApp(environment.firebaseKeys)),
    provideAuth(() => getAuth(getApp())),
    provideQueryClient(
      () =>
        new QueryClient({
          defaultOptions: {
            queries: {
              staleTime: 1000 * 60 * 2,
              retry: 0,
            },
          },
        }),
    ),
    importProvidersFrom(LoggerModule.forRoot({ level: environment.loggerLevel })),
    importProvidersFrom(MatSnackBarModule, MatDialogModule),
    DatePipe,
    { provide: DEFAULT_TIMEOUT, useValue: 10000 },
    { provide: ARIA_TOAST_SERVICE, useClass: AriaToastrInformationService },
    { provide: ErrorHandler, useClass: HttpDevToolsErrorHandler },
    provideEnvironmentInitializer(() => inject(HttpDevToolsService).registerGlobalErrorListeners()),
    provideEnvironmentInitializer(() => clearLegacyCacheKeys()),
  ],
};
